import { useNavigate } from 'react-router-dom'
import { Icon } from '../components/Layout'
import { useAuth } from '../context/AuthContext'
import CisLevelTable from '../components/CisLevelTable'
import CisCptExplainer from '../components/CisCptExplainer'
import { getLevelForCis, getNextLevel, formatMultiplier } from '../utils/cisLevels'

// The "level info page" referenced in item 8 — one place users can go to
// see level thresholds, reward multipliers, and how CIS relates to CPT.
// Linked from Profile and Offerwall.
export default function LevelInfo() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const cis = Number(user?.cisScore ?? 0)
  const currentLevel = getLevelForCis(cis)
  const nextLevel = getNextLevel(cis)
  const ptsToNext = nextLevel ? Math.max(0, nextLevel.min - cis) : 0

  return (
    <div className="flex-1 lg:ml-[300px] w-full max-w-4xl flex flex-col gap-3 lg:gap-5 px-4 lg:px-0 pt-4 lg:pt-0 pb-24 md:pb-8">
      <section className="border border-on-background/10 lg:neo-border lg:neo-shadow p-4 lg:p-8 bg-surface-container">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-[11px] font-bold text-on-surface-variant hover:text-on-background mb-3"
        >
          <Icon name="arrow_back" className="text-[16px]" /> Back
        </button>
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary-container mb-1">CIS Levels</p>
        <h1 className="text-[1.6rem] lg:text-display-lg font-extrabold uppercase tracking-tight leading-none mb-2">
          Levels &amp; Reward Multipliers
        </h1>
        <p className="text-sm text-on-surface-variant max-w-xl">
          Your CIS (Chicago Influence Score) determines your level. Your level sets the CPT reward multiplier you
          earn on offers, campaigns, and tasks.
        </p>
      </section>

      {user && (
        <section className="border-2 border-on-background bg-on-background text-inverse-on-surface p-4 lg:p-6 flex items-center justify-between gap-3">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest opacity-60 mb-1">Your Level</p>
            <p className={`text-[20px] font-extrabold uppercase ${currentLevel.color}`}>{currentLevel.label}</p>
            <p className="text-[11px] opacity-70 mt-0.5">{cis.toLocaleString()} CIS · {formatMultiplier(currentLevel.multiplier)} rewards</p>
          </div>
          {nextLevel && (
            <div className="text-right">
              <p className="text-[10px] font-bold uppercase tracking-widest opacity-60 mb-1">Next Level</p>
              <p className="text-[13px] font-bold">{nextLevel.label}</p>
              <p className="text-[11px] opacity-70 mt-0.5">
                +{ptsToNext.toLocaleString()} CIS to go
              </p>
            </div>
          )}
        </section>
      )}

      <section className="flex flex-col gap-2">
        <h2 className="font-extrabold text-[13px] uppercase tracking-widest px-1">Level Thresholds &amp; Multipliers</h2>
        <CisLevelTable currentLevelKey={currentLevel.key} />
        <p className="text-[10px] text-on-surface-variant px-1">
          Example: a base reward of 100 CPT pays 100 CPT at Bronze, 110 at Silver, 125 at Gold, and 150 at Diamond.
          The same multiplier applies across CPX offers, campaigns, third-party ads, Bonus Hunts, and tasks.
        </p>
      </section>

      <CisCptExplainer defaultOpen />
    </div>
  )
}
